import type { MetricFilterAdapter } from "@/metrics/runtime-adapters"

import { type MetricValueFilter as MetricFilter } from "@/lib/metricValueFilters"
import type { RunRowLike } from "@/types/RunRow"

export type MetricFilterRecord = Pick<
  MetricFilterAdapter,
  "getHighlightValueForMetricOutput" | "id" | "metricScale"
>

function getFilterScale(metric: MetricFilterRecord): number {
  return metric.metricScale ?? 1
}

/**
 * Filters run rows based on metric filters
 */
export function applyMetricFilters(
  runRows: RunRowLike[],
  filters: MetricFilter[],
  metricRecordsById: Record<string, MetricFilterRecord>,
): RunRowLike[] {
  if (filters.length === 0) {
    return runRows
  }

  return runRows.filter((row) => {
    // Row must satisfy ANY filter (OR logic)
    return filters.some((filter) => {
      const metric = metricRecordsById[filter.metricName]
      if (!metric) {
        return false
      }

      const metricValue = row.metric_outputs?.[metric.id]

      // Handle "exists" operator specially
      if (filter.operator === "exists") {
        return metricValue !== undefined && metricValue !== null
      }

      // For other operators, we need a value to compare
      if (metricValue === undefined || metricValue === null) {
        return false
      }

      // Handle string filters
      if (filter.valueType === "string") {
        let stringValue: string | null = null
        if (typeof metricValue === "object" && metricValue !== null) {
          if (
            metricValue.output_type === "string" &&
            metricValue.string_value
          ) {
            stringValue = metricValue.string_value
          }
        } else if (typeof metricValue === "string") {
          stringValue = metricValue
        }

        if (stringValue === null || filter.value === null) {
          return false
        }

        // Apply string comparison
        switch (filter.operator) {
          case "=":
            return stringValue === filter.value
          case "!=":
            return stringValue !== filter.value
          case ">":
          case "<":
          case ">=":
          case "<=":
          case "range":
          default:
            return false
        }
      }

      // Handle numeric filters. Read via the metric record so composite metrics
      // resolve to the per-method summary stat instead of raw float_value.
      let numericValue: number | null = null
      if (typeof metricValue === "object" && metricValue !== null) {
        if (metricValue.output_type === "float") {
          const recordValue =
            metric.getHighlightValueForMetricOutput(metricValue)
          if (typeof recordValue === "number") {
            numericValue = recordValue
          }
        }
      } else if (typeof metricValue === "number") {
        numericValue = metricValue
      } else if (typeof metricValue === "boolean") {
        numericValue = metricValue ? 1 : 0
      }

      if (numericValue === null || filter.value === null) {
        return false
      }

      numericValue *= getFilterScale(metric)

      // Apply the comparison operator
      switch (filter.operator) {
        case "range":
          return (
            typeof filter.rangeMax === "number" &&
            numericValue >= (filter.value as number) &&
            numericValue <= filter.rangeMax
          )
        case ">":
          return numericValue > (filter.value as number)
        case "<":
          return numericValue < (filter.value as number)
        case "=":
          return Math.abs(numericValue - (filter.value as number)) < 0.0001 // Float comparison
        case ">=":
          return numericValue >= (filter.value as number)
        case "<=":
          return numericValue <= (filter.value as number)
        case "!=":
          return Math.abs(numericValue - (filter.value as number)) >= 0.0001
        default:
          return true
      }
    })
  })
}
